import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const orig = error.config || {};
    const url = orig.url || "";
    const skip = url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/register");
    if (error.response?.status === 401 && !orig._retry && !skip) {
      orig._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(orig);
      } catch (e) {
        refreshing = null;
      }
    }
    return Promise.reject(error);
  }
);

export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(" ");
  return d.msg || String(d);
}

export function money(amount, currency = "GBP") {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency || "GBP" }).format(Number(amount || 0));
}

export function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d.length <= 10 ? `${d}T00:00:00` : d);
  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export function useFetch(url, silent = false) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    if (!url) return;
    setLoading(true);
    try {
      const r = await api.get(url);
      setData(r.data);
    } catch (e) {
      setData(null);
      if (!silent) toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [url, silent]);
  useEffect(() => {
    reload();
  }, [reload]);
  return { data, loading, reload, setData };
}

export async function openFile(url) {
  const r = await api.get(url, { responseType: "blob" });
  const blobUrl = URL.createObjectURL(r.data);
  window.open(blobUrl, "_blank");
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
}

export async function downloadFile(url, filename) {
  let r;
  try {
    r = await api.get(url, { responseType: "blob" });
  } catch (e) {
    if (e?.response?.data instanceof Blob) {
      try { e.response.data = JSON.parse(await e.response.data.text()); } catch (x) { /* keep original */ }
    }
    throw e;
  }
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 60000);
}

export const CURRENCIES = ["GBP", "EUR", "USD", "CAD", "AUD", "NZD", "ZAR", "AED", "INR"];
export const CERT_LABELS = {
  gas_safety: "Gas Safety (CP12)",
  eicr: "EICR (Electrical)",
  epc: "EPC",
  fire_alarm: "Smoke & CO alarms",
  legionella: "Legionella risk",
  hmo_licence: "HMO licence",
  insurance: "Landlord insurance",
  other: "Other",
};
